import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField, MenuItem, Autocomplete, Alert, CircularProgress } from '@mui/material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createWorkOrder, type CreateWorkOrderPayload } from '../../api/mentenanta'
import { listEquipment } from '../../api/echipamente'
import { listUsers } from '../../api/admin'
import { errorMessage } from '../../api/client'

const schema = z.object({
  equipmentId: z.string().min(1, 'Selectați echipamentul'),
  type: z.enum(['Corectiv', 'Preventiv']),
  priority: z.enum(['Scazuta', 'Normala', 'Ridicata', 'Urgenta']),
  description: z.string().min(1, 'Descrierea este obligatorie'),
  assignedToUserId: z.string().nullable(),
})
type FormValues = z.infer<typeof schema>

const EMPTY: FormValues = { equipmentId: '', type: 'Corectiv', priority: 'Normala', description: '', assignedToUserId: null }

export default function WorkOrderFormDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient()
  const { data: equipment } = useQuery({ queryKey: ['mentenanta', 'echipamente', 'utilaje'], queryFn: listEquipment, enabled: open })
  const { data: users } = useQuery({ queryKey: ['admin', 'users'], queryFn: () => listUsers(), enabled: open })

  const { control, handleSubmit, reset, formState: { errors } } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: EMPTY })

  const mutation = useMutation({
    mutationFn: (v: FormValues) => createWorkOrder(v as CreateWorkOrderPayload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['mentenanta', 'ordine-lucru'] })
      void qc.invalidateQueries({ queryKey: ['mentenanta', 'echipamente'] })
      reset(EMPTY)
      onClose()
    },
  })

  const handleClose = () => { mutation.reset(); reset(EMPTY); onClose() }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit((v) => mutation.mutate(v))}>
        <DialogTitle>Ordin de lucru nou</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Controller name="equipmentId" control={control} render={({ field }) => (
              <Autocomplete
                options={equipment?.map((e) => e.id) ?? []}
                getOptionLabel={(id) => { const e = equipment?.find((x) => x.id === id); return e ? `${e.code} — ${e.name}` : id }}
                value={field.value || null} onChange={(_, v) => field.onChange(v ?? '')}
                renderInput={(params) => <TextField {...params} label="Echipament" error={!!errors.equipmentId} helperText={errors.equipmentId?.message} />}
              />
            )} />
            <Stack direction="row" spacing={2}>
              <Controller name="type" control={control} render={({ field }) => (
                <TextField {...field} select label="Tip" sx={{ width: 160 }} error={!!errors.type} helperText={errors.type?.message}>
                  <MenuItem value="Corectiv">Corectiv</MenuItem>
                  <MenuItem value="Preventiv">Preventiv</MenuItem>
                </TextField>
              )} />
              <Controller name="priority" control={control} render={({ field }) => (
                <TextField {...field} select label="Prioritate" sx={{ width: 160 }} error={!!errors.priority} helperText={errors.priority?.message}>
                  <MenuItem value="Scazuta">Scăzută</MenuItem>
                  <MenuItem value="Normala">Normală</MenuItem>
                  <MenuItem value="Ridicata">Ridicată</MenuItem>
                  <MenuItem value="Urgenta">Urgentă</MenuItem>
                </TextField>
              )} />
              <Controller name="assignedToUserId" control={control} render={({ field }) => (
                <Autocomplete
                  sx={{ flexGrow: 1 }} options={users?.map((u) => u.id) ?? []}
                  getOptionLabel={(id) => { const u = users?.find((x) => x.id === id); return u ? `${u.firstName} ${u.lastName}` : id }}
                  value={field.value} onChange={(_, v) => field.onChange(v ?? null)}
                  renderInput={(params) => <TextField {...params} label="Tehnician (opțional)" />}
                />
              )} />
            </Stack>
            <Controller name="description" control={control} render={({ field }) => (
              <TextField {...field} label="Descriere incident" fullWidth multiline minRows={3}
                error={!!errors.description} helperText={errors.description?.message} />
            )} />
            {Object.keys(errors).length > 0 && (
              <Alert severity="warning">Există câmpuri obligatorii necompletate — verificați câmpurile marcate cu roșu mai sus.</Alert>
            )}
            {mutation.isError && <Alert severity="error">{errorMessage(mutation.error)}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={handleClose}>Anulează</Button>
          <Button type="submit" variant="contained" disabled={mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}>
            Deschide
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
